import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Edit, Trash2, Upload, Users, Download } from 'lucide-react';
import Button from '@components/atoms/ui-kit/button.tsx';
import { usePageHeader } from '@utils/hooks/use-page-header';
import DataGridV2 from '@components/atoms/datatable/data-grid-v2';
import type { GridAction } from '@components/atoms/datatable/grid-action-controller';
import SpeakerFormModal from '@components/speakers/speaker-form-modal.tsx';
import DeleteSpeakerModal from '@components/speakers/delete-speaker-modal.tsx';
import BulkUploadModal from '@components/speakers/bulk-upload-modal.tsx';
import {
  type SpeakerRow,
  useSpeakerColumns
} from '@components/speakers/speaker-columns.tsx';
import { useGetSpeakers } from '@utils/api/organizations/speakers';
import {
  Speaker,
  SpeakerListResponse
} from '@utils/types/organization/speaker.ts';
import { useNewAppSearchParams } from '@utils/hooks/use-new-app-search-params.ts';
import { Text } from '@components/atoms/ui-kit/text.tsx';
import { Axios } from '@utils/api/axios-instance.ts';
import { GET_URL_NEW } from '@utils/api/endpoints.ts';
import writeXlsxFile from 'write-excel-file/browser';
import toast from 'react-hot-toast';

export default function Speakers() {
  const { t } = useTranslation();
  const { searchParams } = useNewAppSearchParams();
  const columns = useSpeakerColumns();

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isBulkUploadModalOpen, setIsBulkUploadModalOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [selectedSpeaker, setSelectedSpeaker] = useState<Speaker | null>(null);

  const offset = Number(searchParams.offset) || 0;
  const limit = Number(searchParams.limit) || 25;
  const q = (searchParams.q as string) || '';
  const sort = (searchParams.sort as string) || 'name';
  const direction = ((searchParams.direction as string) || 'asc') as
    | 'asc'
    | 'desc';

  const { data, isLoading } = useGetSpeakers({
    offset,
    limit,
    q,
    sort,
    direction
  });

  const rows = useMemo<SpeakerRow[]>(() => {
    if (!data?.result) return [];
    return data.result.map((speaker: Speaker) => ({
      id: speaker.id,
      name: speaker.name,
      role: speaker.role || '',
      party: speaker.party || '',
      email: speaker.email || '',
      notes: speaker.notes || ''
    }));
  }, [data]);

  const handleExport = async () => {
    setIsExporting(true);
    try {
      // Fetch all speakers matching the current search/sort in one call.
      // Use total_results from the already-loaded page as the limit so we
      // don't need a separate pagination loop.
      const total = data?.total_results ?? 10000;
      const url = GET_URL_NEW({
        offset: 0,
        limit: total,
        q,
        sort,
        direction
      }).speakers;
      const { data: res } = await Axios.get<SpeakerListResponse>(url);
      const speakers = res.result ?? [];

      const rows: string[][] = [
        ['name', 'role', 'party', 'email', 'notes'],
        ...speakers.map(s => [
          s.name,
          s.role ?? '',
          s.party ?? '',
          s.email ?? '',
          s.notes ?? ''
        ])
      ];

      const result = await writeXlsxFile(rows);
      await result.toFile('speakers-export.xlsx');
    } catch {
      toast.error(t('export-failed'));
    } finally {
      setIsExporting(false);
    }
  };

  const handleEdit = (speaker: Speaker) => {
    setSelectedSpeaker(speaker);
    setIsEditModalOpen(true);
  };

  const handleDelete = (speaker: Speaker) => {
    setSelectedSpeaker(speaker);
    setIsDeleteModalOpen(true);
  };

  const actions = useMemo<GridAction<SpeakerRow>[]>(() => {
    return [
      {
        id: 'edit',
        label: t('edit'),
        icon: <Edit className="h-3.5 w-3.5" />,
        run: row => {
          const speaker = data?.result.find(s => s.id === row.id);
          if (speaker) handleEdit(speaker);
        }
      },
      {
        id: 'delete',
        label: t('delete'),
        icon: <Trash2 className="h-3.5 w-3.5" />,
        intent: 'danger',
        run: row => {
          const speaker = data?.result.find(s => s.id === row.id);
          if (speaker) handleDelete(speaker);
        }
      }
    ];
  }, [t, data]);

  const showEmptyState = rows.length === 0 && !isLoading && !!data;

  usePageHeader({
    title: t('speaker-directory'),
    description: t('manage-speakers-for-transcripts'),
    actions: (
      <div className="flex gap-2">
        <Button
          variant="secondary"
          onClick={() => void handleExport()}
          label={t('export')}
          icon={<Download width={14} />}
          loading={isExporting}
          disabled={isExporting || rows.length === 0}
        />
        <Button
          variant="secondary"
          onClick={() => setIsBulkUploadModalOpen(true)}
          label={t('bulk-upload')}
          icon={<Upload width={14} />}
        />
        <Button
          onClick={() => setIsCreateModalOpen(true)}
          label={t('add-speaker')}
          icon={<Plus width={14} />}
        />
      </div>
    )
  });

  return (
    <div className="bg-app min-h-screen">
      <SpeakerFormModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        speaker={null}
      />

      <SpeakerFormModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedSpeaker(null);
        }}
        speaker={selectedSpeaker}
      />

      <DeleteSpeakerModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setSelectedSpeaker(null);
        }}
        speaker={selectedSpeaker}
        onDeleteSuccess={() => setSelectedSpeaker(null)}
      />

      <BulkUploadModal
        isOpen={isBulkUploadModalOpen}
        onClose={() => setIsBulkUploadModalOpen(false)}
      />

      <div className="pb-4 sm:pb-0 relative">
        <DataGridV2<SpeakerRow>
          data={rows}
          columns={columns}
          actions={actions}
          enableRowSelection={false}
          enablePagination={true}
          enableSorting={true}
          enableFiltering={true}
          showColumnFilters={false}
          filterMode="popover"
          height="88vh"
          dense
          grid={false}
          striped
          loading={isLoading}
          getRowId={row => String(row.id)}
          totalItems={data?.total_results ?? 0}
          manualPagination
          manualSorting
          manualFiltering
        />

        {showEmptyState && !q && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 pointer-events-none">
            <div className="pointer-events-auto flex flex-col items-center gap-3 mt-8">
              <div className="h-16 w-16 rounded-full border-2 border-dashed border-hairline flex items-center justify-center">
                <Users className="h-7 w-7" />
              </div>
              <Text as="p" size="sm" tone="muted">
                {t('no-speakers-yet')}
              </Text>
              <Button
                label={t('add-first-speaker')}
                icon={<Plus width={14} />}
                onClick={() => setIsCreateModalOpen(true)}
                variant="primary"
                size="small"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
